import moment from "moment";
import { LoadState } from "../../../constants/enums";
import { ICommAutomobileDriverClaims, ICommAutomobileDriverConviction, ICommAutomobileDriverInformation } from "../commAutoDriver/commAutoDriver.types";
import { ICommAutomobileInterestedParty, ICommAutomobileVehicleInformation } from "../commAutoVehicle/commAutoVehicle.types";
import { ICommAutomobileScheduleA } from "../commAutoSchedule/commAutoSchedule.types";
import { STANDARD_APP_DATE_FORMAT } from "../../../constants/constants";
import { getUniqueId } from "../../../helpers";
import { ITemplateField } from "../../CustomFormFields/CustomFormFields.types";

export interface ICommAutoPolicyChangeRequestState {
  list: ICommAutoPolicyChange[];
  policyChangeData: ICommAutoPolicyChange;
  totalRecords: number;
  loading: LoadState;
  error: string | null;
}

export interface IAutomobileAdditionalInterest {
  key: string;
  name: string | null;
  code: string | null;
  address: string | null;
  unit_or_suite: string | null;
  city: string | null;
  province_or_state: string | null;
  postal_code: string | null;
  country: string | null;
  nature_of_interest: string | null;
  vehicle_number: string | null;
  interested_party_code: string | null;
}

// V2 only: captures the "NEW" values a user enters for a single vehicle row
// in the Address Change panel (parking / commute / annual km / use /
// principal driver / occasional driver). `selected` tracks the row checkbox;
// only checked vehicles are included when the request is submitted.
export interface ICommAutoAddressChangeVehicleUpdate {
  vehicle_number: number | null;
  selected: boolean;
  parking: string | null;
  commute: string | null;
  annual_km: string | null;
  use: string | null;
  principal_driver: string | null;
  occasional_driver: string | null;
}

// V2 only: "Driver Assignment" — assigns a driver to a vehicle as either the
// Principal or Occasional operator, with the percentage of use for that unit
// (percentages on a unit should total 100%). Supports both Add (a brand new
// assignment) and Change (substituting an existing assignment for the same
// vehicle/driver pairing), mirroring the vehicle/driver/interested-party
// substitution pattern.
export interface ICommAutoDriverAssignment {
  vehicle_number: number | null;
  driver_number: number | null;
  assignment_type: "Principal" | "Occasional" | null;
  percentage: number | null;
  notes: string | null;
  mode?: "Add" | "Change" | null;
  delete_driver_assignment?: string | null;
}

export interface ICommAutoPolicyChange {
  incremental_number: number | null;
  customer_id: number | null;
  customer_policy_id: number | null;
  customer_name: string | null;
  customer_code: string | null;
  cancellation_reason: string | null;
  policy_type: string;
  branch_code: string | null;
  producer_one_code: string | null;
  producer_one_id: string | null;
  policy_status: string | null;
  policy_number: string | null;
  insurer: string | null;
  policy_effective_date: string | null;
  effective_time: string | null;
  expiry_date: string | null;
  type: string | null;
  sub_type: string | null;
  // date_change: string | null;
  // time_change: string | null;
  date_created: string | null;
  effective_date: string | null;
  days_left: string | null;
  due_date: string | null;
  date_completed: string | null;
  assigned_to_id: string;
  assigned_to_name: string | null;
  current_policy_status: string | null;
  credit_card_info: {
    // V2 only: which kind of payment method the change is for. Drives which
    // fields the V2 form shows: "Bank" (PAD), "Credit Card", "Cheque", or
    // "Other".
    payment_type?: string | null;
    cc: number | null;
    expiry: string | null;
    cvv: number | null;
    account_holder_name: string | null;
    bank_name: string | null;
    institution_number: string | null;
    transit_number: string | null;
    account_number: string | null;
    // V2 only: how the authorization was captured: "G4Sign" or "OnFile".
    signature_type?: string | null;
  };
  name_change: {
    new_name: string | null;
    // V2 only.
    date_of_birth?: string | null;
    relationship?: string | null;
  };
  address_change: {
    address_type: string | null;
    // V2 only: which parts of the address are changing — mirrors the
    // "Mailing only / Garaging only / Mailing and garaging" pill selector.
    change_type?:
      | "Mailing only"
      | "Garaging only"
      | "Mailing and garaging"
      | null;
    address: string | null;
    address_two: string | null;
    // V2 only.
    unit_number?: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
    // V2 only: effective date and contact details captured on the Address
    // Change panel.
    effective_date?: string | null;
    phone?: string | null;
    email?: string | null;
    // V2 only: per-vehicle re-rate fields shown in the Address Change panel.
    vehicle_updates?: ICommAutoAddressChangeVehicleUpdate[];
  };
  change_mailing_address_on_customer: string | null;
  driver_change: {
    type: string | null;
    delete_driver: string | null;
    // V2 only: snapshot of the replaced driver's identifying details.
    delete_driver_details?: {
      driver_number: string | null;
      driver_name: string | null;
      license_number: string | null;
    } | null;
    records: ICommAutomobileDriverInformation[]; //JSON
  };
  driver_conviction_change: {
    type: string | null;
    delete_driver_conviction: string | null;
    records: ICommAutomobileDriverConviction[]; //JSON
  };
  driver_claim_change: {
    type: string | null;
    delete_driver_claim: string | null;
    records: ICommAutomobileDriverClaims[]; //JSON
  };
  vehicle_change: {
    type: string | null;
    delete_vehicle: string | null;
    // V2 only: snapshot of the replaced vehicle's identifying details.
    delete_vehicle_details?: {
      vehicle_number: string | null;
      year: string | null;
      make: string | null;
      model: string | null;
      vin: string | null;
    } | null;
    records: ICommAutomobileVehicleInformation[]; //JSON
  };
  interested_party_change: {
    type: string | null;
    delete_interested_party: string | null;
    // V2 only: snapshot of the replaced interested party's identifying details.
    delete_interested_party_details?: {
      number: string | null;
      interested_party_code: string | null;
      name: string | null;
    } | null;
    records: ICommAutomobileInterestedParty[]; //JSON
  };
  coverage_change: {
    type: string | null;
    delete_coverage: string | null;
    // V2 only: snapshot of the replaced coverage's identifying details.
    delete_coverage_details?: {
      incremental_number: string | null;
      coverage_name: string | null;
      vin: string | null;
    } | null;
    records: ICommAutomobileScheduleA[]; //JSON
  };
  // V2 only: driver assignment change section.
  driver_assignment_change?: {
    type: string | null;
    delete_driver_assignment: string | null;
    records: ICommAutoDriverAssignment[]; //JSON
  };
  attachments: {
    key: string;
    description: string | null;
    status: string | null;
    url: string | null;
    file: File | null;
  }[];
  underwriting: ITemplateField[][];
  remarks: string | null;
  status: string;
  change_request_code?: string;
  assigned_policy_number: string | null;
}

export const initialCommAutoPolicyChangeReqState: ICommAutoPolicyChangeRequestState =
  {
    policyChangeData: {
      incremental_number: null,
      customer_id: null,
      customer_policy_id: null,
      customer_name: null,
      customer_code: null,
      cancellation_reason: null,
      policy_type: "",
      branch_code: null,
      producer_one_code: null,
      producer_one_id: null,
      policy_status: null,
      insurer: null,
      policy_number: null,
      policy_effective_date: null,
      effective_time: null,
      expiry_date: moment().hours(0).minutes(1).format(STANDARD_APP_DATE_FORMAT),
      type: "PolicyChange",
      sub_type: "Name Change",
      // date_change: moment().format(STANDARD_APP_DATE_FORMAT),
      // time_change: moment().format(),
      date_created: moment().format(STANDARD_APP_DATE_FORMAT),
       effective_date: moment().startOf('day').add(1, 'minute').format("YYYY-MM-DD HH:mm:ss"),
      // effective_date: moment()
      //   .startOf("day")
      //   .add(1, "minute")
      //   .format("YYYY-MM-DD HH:mm:ss"),
      days_left: null,
      due_date: moment().add(5, "days").format(STANDARD_APP_DATE_FORMAT),
      date_completed: null,
      assigned_to_id: "",
      credit_card_info: {
        payment_type: null,
        cc: null,
        expiry: null,
        cvv: null,
        account_holder_name: null,
        bank_name: null,
        institution_number: null,
        transit_number: null,
        account_number: null,
        signature_type: null,
      },
      name_change: {
        new_name: null,
        date_of_birth: null,
        relationship: null,
      },
      address_change: {
        address_type: null,
        change_type: null,
        address: null,
        address_two: null,
        unit_number: null,
        city: null,
        state: null,
        postal_code: null,
        country: null,
        effective_date: null,
        phone: null,
        email: null,
        vehicle_updates: [],
      },
      change_mailing_address_on_customer: null,
      driver_change: {
        type: null,
        delete_driver: null,
        delete_driver_details: null,
        records: [],
      },
      driver_conviction_change: {
        type: null,
        delete_driver_conviction: null,
        records: [],
      },
      driver_claim_change: {
        type: null,
        delete_driver_claim: null,
        records: [],
      },
      vehicle_change: {
        delete_vehicle: null,
        delete_vehicle_details: null,
        type: null,
        records: [],
      },
      coverage_change: {
        delete_coverage: null,
        delete_coverage_details: null,
        type: null,
        records: [],
      },
      interested_party_change: {
        type: null,
        delete_interested_party: null,
        delete_interested_party_details: null,
        records: [],
      },
      driver_assignment_change: {
        type: null,
        delete_driver_assignment: null,
        records: [],
      },

      underwriting: [],
      remarks: null,
      attachments: [
        {
          key: getUniqueId(),
          description: null,
          status: "Pending",
          url: null,
          file: null,
        },
      ],
      status: "Active",
      current_policy_status: null,
      assigned_to_name: null,
      assigned_policy_number: null,
    },
    list: [],
    totalRecords: 0,
    loading: LoadState.NotLoaded,
    error: null,
  };
